import React, { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import MarkerClusterGroup from 'react-leaflet-cluster';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { OSM_TILE_URL, OSM_ATTRIBUTION, toCoord, hasCoords, CLUSTER_OPTIONS } from '../components/map/mapUtils';
import { displayJob } from '../lib/jobs';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { GoogleReviewsSection } from '../components/home/GoogleReviewsSection';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { Switch } from '../components/ui/switch';
import {
  Building2, 
  HardHat, 
  Search, 
  MapPin, 
  Star, 
  Clock,
  Users,
  FileCheck,
  MessageSquare,
  ArrowRight,
  Briefcase,
  CheckCircle2,
  Zap
} from 'lucide-react';
import { SEOHead, organizationSchema, websiteSchema } from '../components/SEOHead';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Fix Leaflet default icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// Custom marker icons
const jobIcon = new L.DivIcon({
  className: 'custom-marker',
  html: '<div style="background-color: #1E3A8A; width: 24px; height: 24px; border-radius: 50%; border: 3px solid white; box-shadow: 0 2px 4px rgba(0,0,0,0.3);"></div>',
  iconSize: [24, 24],
  iconAnchor: [12, 12],
});

const contractorIcon = new L.DivIcon({
  className: 'custom-marker',
  html: '<div style="background-color: #F59E0B; width: 24px; height: 24px; border-radius: 50%; border: 3px solid white; box-shadow: 0 2px 4px rgba(0,0,0,0.3);"></div>',
  iconSize: [24, 24],
  iconAnchor: [12, 12],
});

const urgentIcon = new L.DivIcon({
  className: 'custom-marker',
  html: '<div style="background-color: #EF4444; width: 24px; height: 24px; border-radius: 50%; border: 3px solid white; box-shadow: 0 2px 4px rgba(0,0,0,0.3); animation: pulse 2s infinite;"></div>',
  iconSize: [24, 24],
  iconAnchor: [12, 12],
});

const companyIcon = new L.DivIcon({
  className: 'custom-marker',
  html: '<div style="background-color: #10B981; width: 24px; height: 24px; border-radius: 50%; border: 3px solid white; box-shadow: 0 2px 4px rgba(0,0,0,0.3);"></div>',
  iconSize: [24, 24],
  iconAnchor: [12, 12],
});

const bothIcon = new L.DivIcon({
  className: 'custom-marker',
  html: '<div style="width: 24px; height: 24px; border-radius: 50%; border: 3px solid white; box-shadow: 0 2px 4px rgba(0,0,0,0.3); background: linear-gradient(90deg, #F59E0B 50%, #10B981 50%);"></div>',
  iconSize: [24, 24],
  iconAnchor: [12, 12],
});

// Map bounds setter component
const MapBounds = ({ markers }) => {
  const map = useMap();
  
  useEffect(() => {
    if (markers.length > 0) {
      const bounds = L.latLngBounds(markers.map(m => [m.lat, m.lng]));
      map.fitBounds(bounds, { padding: [50, 50], maxZoom: 12 });
    }
  }, [markers, map]);
  
  return null;
};

const HomePage = () => {
  const { t } = useTranslation();
  const [mapData, setMapData] = useState({ jobs: [], contractors: [], companies: [] });
  const [latestJobs, setLatestJobs] = useState([]);
  const [featuredContractors, setFeaturedContractors] = useState([]);
  const [latestUsers, setLatestUsers] = useState([]);
  const [showJobs, setShowJobs] = useState(true);
  const [showContractors, setShowContractors] = useState(true);
  const [showCompanies, setShowCompanies] = useState(true);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [mapRes, jobsRes, contractorsRes, latestUsersRes] = await Promise.all([
          axios.get(`${API}/map/data`),
          axios.get(`${API}/jobs?limit=6`),
          axios.get(`${API}/contractors?limit=4&is_verified=true`),
          axios.get(`${API}/latest-users?limit=8`)
        ]);
        setMapData(mapRes.data);
        setLatestJobs(jobsRes.data.jobs || []);
        setFeaturedContractors(contractorsRes.data.contractors || []);
        setLatestUsers(latestUsersRes.data || []);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const markers = useMemo(() => {
    const result = [];
    if (showJobs) {
      mapData.jobs.forEach(job => {
        if (hasCoords(job)) {
          result.push({ lat: toCoord(job.latitude), lng: toCoord(job.longitude), type: 'job', data: job });
        }
      });
    }
    if (showContractors) {
      mapData.contractors.forEach(contractor => {
        if (hasCoords(contractor)) {
          result.push({ 
            lat: toCoord(contractor.latitude), 
            lng: toCoord(contractor.longitude), 
            type: contractor.type === 'both' ? 'both' : 'contractor', 
            data: contractor 
          });
        }
      });
    }
    if (showCompanies) {
      (mapData.companies || []).forEach(company => {
        if (hasCoords(company)) {
          // Avoid duplicating "both" users already shown in contractors
          const alreadyShown = showContractors && company.type === 'both';
          if (!alreadyShown) {
            result.push({ 
              lat: toCoord(company.latitude), 
              lng: toCoord(company.longitude), 
              type: company.type === 'both' ? 'both' : 'company', 
              data: company 
            });
          }
        }
      });
    }
    return result;
  }, [mapData, showJobs, showContractors, showCompanies]);

  // Default center: Belgium/Luxembourg area
  const defaultCenter = [50.5, 4.5];

  return (
    <div>
      <SEOHead
        path="/"
        jsonLd={[organizationSchema, websiteSchema]}
      />
      {/* Hero Section */}
      <section 
        className="relative min-h-[600px] flex items-center"
        style={{
          backgroundImage: `url('https://images.pexels.com/photos/392031/pexels-photo-392031.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="absolute inset-0 bg-slate-900/70"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <div className="max-w-3xl">
            <p className="text-overline mb-4">BuildConnect</p>
            <h1 className="font-heading font-black text-4xl sm:text-5xl lg:text-6xl text-white tracking-tight mb-6">
              {t('hero.title')}
            </h1>
            <p className="text-lg sm:text-xl text-gray-200 mb-8">
              {t('hero.subtitle')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/jobs">
                <Button className="btn-accent text-lg px-8 py-6 w-full sm:w-auto" data-testid="hero-find-job">
                  <Briefcase className="w-5 h-5 mr-2" />
                  {t('hero.findJob')}
                </Button>
              </Link>
              <Link to="/contractors">
                <Button variant="outline" className="text-lg px-8 py-6 bg-white/10 border-white/30 text-white hover:bg-white hover:text-[#1E3A8A] w-full sm:w-auto" data-testid="hero-find-contractor">
                  <HardHat className="w-5 h-5 mr-2" />
                  {t('hero.findContractor')}
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Interactive Map Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <p className="text-overline mb-2">{t('map.title')}</p>
            <h2 className="font-heading font-bold text-2xl sm:text-3xl lg:text-4xl text-[#111827] tracking-tight">
              Explorez les opportunités près de chez vous
            </h2>
          </div>
          
          {/* Map Controls */}
          <div className="flex justify-center gap-8 mb-6 flex-wrap">
            <div className="flex items-center gap-2">
              <Switch 
                checked={showJobs} 
                onCheckedChange={setShowJobs}
                data-testid="map-toggle-jobs"
              />
              <span className="flex items-center gap-2 text-sm">
                <span className="w-4 h-4 rounded-full bg-[#1E3A8A]"></span>
                {t('map.showJobs')}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <Switch 
                checked={showContractors} 
                onCheckedChange={setShowContractors}
                data-testid="map-toggle-contractors"
              />
              <span className="flex items-center gap-2 text-sm">
                <span className="w-4 h-4 rounded-full bg-[#F59E0B]"></span>
                {t('map.showContractors')}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <Switch 
                checked={showCompanies} 
                onCheckedChange={setShowCompanies}
                data-testid="map-toggle-companies"
              />
              <span className="flex items-center gap-2 text-sm">
                <span className="w-4 h-4 rounded-full bg-[#10B981]"></span>
                Donneurs d'ordre
              </span>
            </div>
          </div>

          {/* Map */}
          <div className="h-[500px] rounded-md overflow-hidden border border-[#1E3A8A]/10 shadow-lg" data-testid="interactive-map">
            <MapContainer 
              center={defaultCenter} 
              zoom={7} 
              style={{ height: '100%', width: '100%' }}
              scrollWheelZoom={false}
            >
              <TileLayer attribution={OSM_ATTRIBUTION} url={OSM_TILE_URL} />
              <MarkerClusterGroup key={markers.length} {...CLUSTER_OPTIONS}>
              {markers.map((marker, index) => (
                <Marker 
                  key={`${marker.type}-${marker.data.id || index}`}
                  position={[marker.lat, marker.lng]}
                  markerType={marker.type}
                  icon={marker.type === 'job' ? (marker.data.is_urgent ? urgentIcon : jobIcon) : marker.type === 'both' ? bothIcon : marker.type === 'company' ? companyIcon : contractorIcon}
                >
                  <Popup>
                    <div className="min-w-[200px]">
                      {marker.type === 'job' ? (
                        <>
                          <h4 className="font-heading font-bold text-[#1E3A8A]">{marker.data.title}</h4>
                          <p className="text-sm text-[#4B5563] mb-2">
                            {marker.data.postal_code && <span className="font-mono text-xs bg-[#1E3A8A]/10 text-[#1E3A8A] px-1 rounded mr-1">{marker.data.country === 'Belgium' ? 'BE' : marker.data.country === 'France' ? 'FR' : 'LU'}-{marker.data.postal_code}</span>}
                            {marker.data.city}, {marker.data.country}
                          </p>
                          {marker.data.is_urgent && (
                            <Badge className="badge-urgent mb-2">Urgent</Badge>
                          )}
                          {marker.data.status === 'in_progress' && (
                            <Badge className="bg-amber-100 text-amber-700 mb-2 ml-1">En cours</Badge>
                          )}
                          <p className="text-sm">
                            Budget: €{marker.data.budget_min} - €{marker.data.budget_max}
                          </p>
                          <Link to={`/jobs/${marker.data.id}`}>
                            <Button size="sm" className="btn-primary mt-2 w-full">
                              {t('common.viewDetails')}
                            </Button>
                          </Link>
                        </>
                      ) : marker.type === 'both' ? (
                        <>
                          <h4 className="font-heading font-bold" style={{background: 'linear-gradient(90deg, #F59E0B 50%, #10B981 50%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent'}}>{marker.data.company_name}</h4>
                          <p className="text-sm text-[#4B5563] mb-2">{marker.data.city}</p>
                          <div className="flex gap-1 mb-2">
                            <Badge className="bg-[#F59E0B]/10 text-[#F59E0B] text-xs">Sous-traitant</Badge>
                            <Badge className="bg-emerald-100 text-emerald-700 text-xs">Donneur d'ordre</Badge>
                          </div>
                          <Link to={`/contractors/${marker.data.id}`}>
                            <Button size="sm" className="btn-accent mt-2 w-full">
                              {t('common.viewProfile')}
                            </Button>
                          </Link>
                        </>
                      ) : marker.type === 'company' ? (
                        <>
                          <h4 className="font-heading font-bold text-[#10B981]">{marker.data.company_name}</h4>
                          <p className="text-sm text-[#4B5563] mb-2">{marker.data.city}</p>
                          <p className="text-xs text-[#6B7280]">Donneur d'ordre</p>
                        </>
                      ) : (
                        <>
                          <h4 className="font-heading font-bold text-[#F59E0B]">{marker.data.company_name}</h4>
                          <p className="text-sm text-[#4B5563] mb-2">{marker.data.city}</p>
                          <div className="flex items-center gap-1 mb-2">
                            <Star className="w-4 h-4 text-[#F59E0B] fill-[#F59E0B]" />
                            <span className="text-sm">{marker.data.rating?.toFixed(1) || '0.0'}</span>
                          </div>
                          <Link to={`/contractors/${marker.data.id}`}>
                            <Button size="sm" className="btn-accent mt-2 w-full">
                              {t('common.viewProfile')}
                            </Button>
                          </Link>
                        </>
                      )}
                    </div>
                  </Popup>
                </Marker>
              ))}
              </MarkerClusterGroup>
              {markers.length > 0 && <MapBounds markers={markers} />}
            </MapContainer>
          </div>
        </div>
      </section>

      {/* Latest Jobs Section */}
      <section className="py-16 bg-[#F9FAFB]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between mb-8">
            <div>
              <p className="text-overline mb-2">{t('jobs.title')}</p>
              <h2 className="font-heading font-bold text-2xl sm:text-3xl text-[#111827] tracking-tight">
                Derniers chantiers publiés
              </h2>
            </div>
            <Link to="/jobs">
              <Button variant="outline" className="gap-2" data-testid="view-all-jobs">
                Voir tout <ArrowRight className="w-4 h-4" />
              </Button>
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {latestJobs.map((raw) => {
              const job = displayJob(raw, null);
              return (
              <Card className={`card-surface h-full ${job.masked ? 'opacity-70 bg-[#F3F4F6]' : ''}`} key={job.id} data-testid={`job-card-${job.id}`}>
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    {job.trade_category ? <Badge className="badge-trade">{t(`trades.${job.trade_category}`) || job.trade_category}</Badge> : <span />}
                    {job.masked ? <Badge className="bg-red-100 text-red-700 border border-red-300">Expiré</Badge> : job.is_urgent && <Badge className="badge-urgent">{t('common.urgent')}</Badge>}
                  </div>
                  <h3 className={`font-heading font-bold text-lg mb-2 ${job.masked ? 'text-[#6B7280]' : 'text-[#111827]'}`} data-testid={`job-card-title-${job.id}`}>{job.title}</h3>
                  <p className="text-sm text-[#4B5563] mb-4 line-clamp-2">{job.description}</p>
                  <div className="flex items-center gap-2 text-sm text-[#4B5563]">
                    <MapPin className="w-4 h-4 text-[#1E3A8A]" />
                    {job.postal_code && <span className="font-mono text-xs bg-[#1E3A8A]/10 text-[#1E3A8A] px-1 rounded">{job.country === 'Belgium' ? 'BE' : job.country === 'France' ? 'FR' : 'LU'}-{job.postal_code}</span>}
                    {job.city}, {job.country}
                  </div>
                  {!job.masked && (
                  <div className="mt-4 pt-3 border-t border-[#E5E7EB]">
                    <Link to="/register">
                      <Button variant="outline" size="sm" className="w-full gap-2 text-[#1E3A8A] border-[#1E3A8A]/30 hover:bg-[#1E3A8A]/5">
                        Inscrivez-vous pour voir le dossier complet
                      </Button>
                    </Link>
                  </div>
                  )}
                </CardContent>
              </Card>
              );
            })}
          </div>

          {latestJobs.length === 0 && !loading && (
            <p className="text-center text-[#4B5563] py-12">{t('common.noResults')}</p>
          )}
        </div>
      </section>

      {/* Latest Members Section */}
      {latestUsers.length > 0 && (
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="mb-8">
              <p className="text-overline mb-2">COMMUNAUTÉ</p>
              <h2 className="font-heading font-bold text-2xl sm:text-3xl text-[#111827] tracking-tight">
                Derniers inscrits
              </h2>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {latestUsers.map((u) => {
                const isContractorUser = u.user_type === 'contractor' || u.user_type === 'both';
                const isCompanyUser = u.user_type === 'company' || u.user_type === 'both';
                return (
                  <Card key={u.id} className="card-surface" data-testid={`latest-user-${u.id}`}>
                    <CardContent className="p-4 text-center">
                      <div className={`w-12 h-12 rounded-full mx-auto mb-3 flex items-center justify-center ${
                        u.user_type === 'both' ? 'bg-gradient-to-r from-[#F59E0B]/20 to-[#10B981]/20' :
                        isCompanyUser ? 'bg-[#10B981]/10' : 'bg-[#F59E0B]/10'
                      }`}>
                        {isCompanyUser && !isContractorUser ? (
                          <Building2 className="w-6 h-6 text-[#10B981]" />
                        ) : isContractorUser && !isCompanyUser ? (
                          <HardHat className="w-6 h-6 text-[#F59E0B]" />
                        ) : (
                          <Users className="w-6 h-6 text-[#1E3A8A]" />
                        )}
                      </div>
                      <h4 className="font-semibold text-sm text-[#111827] mb-1 truncate">{u.company_name}</h4>
                      <div className="flex justify-center gap-1 mb-2">
                        {isContractorUser && <Badge className="bg-[#F59E0B]/10 text-[#92400E] text-[10px]">Sous-traitant</Badge>}
                        {isCompanyUser && <Badge className="bg-[#10B981]/10 text-[#065F46] text-[10px]">Donneur d'ordre</Badge>}
                      </div>
                      {u.city && (
                        <p className="text-xs text-[#6B7280]">
                          <MapPin className="w-3 h-3 inline mr-0.5" />{u.city}
                        </p>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Featured Contractors Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between mb-8">
            <div>
              <p className="text-overline mb-2">{t('contractors.title')}</p>
              <h2 className="font-heading font-bold text-2xl sm:text-3xl text-[#111827] tracking-tight">
                Sous-traitants vérifiés
              </h2>
            </div>
            <Link to="/contractors">
              <Button variant="outline" className="gap-2" data-testid="view-all-contractors">
                Voir tout <ArrowRight className="w-4 h-4" />
              </Button>
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {featuredContractors.map((contractor) => (
              <Link to={`/contractors/${contractor.id}`} key={contractor.id}>
                <Card className="card-surface h-full" data-testid={`contractor-card-${contractor.id}`}>
                  <CardContent className="p-6 text-center">
                    <div className="w-20 h-20 bg-[#1E3A8A]/10 rounded-full mx-auto mb-4 flex items-center justify-center">
                      <HardHat className="w-10 h-10 text-[#1E3A8A]" />
                    </div>
                    <div className="flex items-center justify-center gap-2 mb-2">
                      <h3 className="font-heading font-bold text-lg text-[#111827]">{contractor.company_name}</h3>
                      {contractor.is_verified && <CheckCircle2 className="w-5 h-5 text-emerald-500" />}
                    </div>
                    <div className="flex items-center justify-center gap-1 mb-3">
                      {contractor.review_count > 0 ? (
                        <>
                          <Star className="w-4 h-4 text-[#F59E0B] fill-[#F59E0B]" />
                          <span className="text-sm font-medium">{contractor.rating?.toFixed(1)}</span>
                          <span className="text-sm text-[#4B5563]">({contractor.review_count} avis)</span>
                        </>
                      ) : (
                        <Badge className="bg-blue-100 text-blue-700 text-xs">Nouveau sur la plateforme</Badge>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-1 justify-center mb-3">
                      {contractor.trades?.slice(0, 2).map((trade, i) => (
                        <Badge key={trade} variant="secondary" className="text-xs">{t(`trades.${trade}`) || trade}</Badge>
                      ))}
                    </div>
                    <p className="text-sm text-[#4B5563]">
                      <MapPin className="w-3 h-3 inline mr-1" />
                      {contractor.city}, {contractor.country}
                    </p>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Completed Projects / Case Studies */}
      <section className="py-16 bg-[#F9FAFB]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <p className="text-overline mb-2">CAS CLIENTS</p>
            <h2 className="font-heading font-bold text-2xl sm:text-3xl text-[#111827] tracking-tight">
              Chantiers réalisés via BuildConnect
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { title: "Rénovation de façade", location: "Liège, Belgique", trade: "Façade", desc: "Nettoyage et rejointoiement complet d'un immeuble de 4 étages en centre-ville." },
              { title: "Construction piscine", location: "Luxembourg", trade: "Piscine", desc: "Installation d'une piscine extérieure avec local technique et terrasse bois." },
              { title: "Isolation toiture", location: "Namur, Belgique", trade: "Isolation", desc: "Isolation complète de la toiture d'une maison unifamiliale en laine de roche." },
            ].map((project) => (
              <Card key={project.title} className="card-surface">
                <CardContent className="p-6">
                  <Badge className="badge-trade mb-3">{project.trade}</Badge>
                  <h3 className="font-heading font-bold text-lg text-[#111827] mb-2">{project.title}</h3>
                  <p className="text-sm text-[#4B5563] mb-4">{project.desc}</p>
                  <div className="flex items-center gap-2 text-xs text-[#6B7280]">
                    <MapPin className="w-3 h-3" />
                    {project.location}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <GoogleReviewsSection />

      {/* How It Works Section */}
      <section className="py-16 bg-[#1E3A8A]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-[#F59E0B] text-sm font-bold uppercase tracking-widest mb-2">{t('howItWorks.title')}</p>
            <h2 className="font-heading font-bold text-2xl sm:text-3xl lg:text-4xl text-white tracking-tight">
              Simple, rapide et efficace
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { icon: Users, title: t('howItWorks.step1Title'), desc: t('howItWorks.step1Desc'), step: '01' },
              { icon: Search, title: t('howItWorks.step2Title'), desc: t('howItWorks.step2Desc'), step: '02' },
              { icon: MessageSquare, title: t('howItWorks.step3Title'), desc: t('howItWorks.step3Desc'), step: '03' },
              { icon: FileCheck, title: t('howItWorks.step4Title'), desc: t('howItWorks.step4Desc'), step: '04' }
            ].map((item) => (
              <div key={item.step} className="text-center">
                <div className="relative mb-6">
                  <div className="w-20 h-20 bg-white/10 rounded-full mx-auto flex items-center justify-center">
                    <item.icon className="w-10 h-10 text-[#F59E0B]" />
                  </div>
                  <span className="absolute -top-2 -right-2 w-8 h-8 bg-[#F59E0B] rounded-full flex items-center justify-center text-sm font-bold text-[#1E3A8A]">
                    {item.step}
                  </span>
                </div>
                <h3 className="font-heading font-bold text-xl text-white mb-3">{item.title}</h3>
                <p className="text-gray-300 text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-[#F9FAFB]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Zap className="w-12 h-12 text-[#F59E0B] mx-auto mb-4" />
          <h2 className="font-heading font-bold text-2xl sm:text-3xl lg:text-4xl text-[#111827] tracking-tight mb-4">
            Prêt à développer votre activité?
          </h2>
          <p className="text-lg text-[#4B5563] mb-8">
            Rejoignez BuildConnect et accédez à des centaines d'opportunités dans le secteur du bâtiment.
          </p>
          <Link to="/register">
            <Button className="btn-primary text-lg px-8 py-6" data-testid="cta-register">
              Créer mon compte gratuitement
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
